import {
  Alert,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Chip,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import ArticleIcon from '@mui/icons-material/Article';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CalculateIcon from '@mui/icons-material/Calculate';
import SchoolIcon from '@mui/icons-material/School';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Resource, SkillMastery, StudentAssessmentFeedback } from '../types';
import MathMarkdown from '../components/MathMarkdown';
import { portalApi } from '../utils/api';

function formatDate(value?: string | null) {
  if (!value) return 'N/A';
  return new Date(value).toLocaleDateString();
}

const masteryColors = {
  mastered: 'success',
  developing: 'warning',
  not_yet_mastered: 'warning',
} as const;

const masteryLabels = {
  mastered: 'Mastered',
  developing: 'Review Recommended',
  not_yet_mastered: 'Review Recommended',
};

const resourceIcons: Record<string, React.ReactNode> = {
  Video: <PlayCircleOutlineIcon />,
  Article: <ArticleIcon />,
  Practice: <CalculateIcon />,
  Worksheet: <AssignmentIcon />,
};

export default function StudentAssessmentFeedbackPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { sectionId, sessionId } = useParams<{ sectionId: string; sessionId: string }>();
  const [showAllSkills, setShowAllSkills] = useState(false);

  const { data: feedback, isLoading, error } = useQuery<StudentAssessmentFeedback>({
    queryKey: ['portal', 'student', 'feedback', sectionId, sessionId],
    queryFn: () => portalApi.getStudentClassFeedback(sectionId!, sessionId!),
    enabled: !!sectionId && !!sessionId,
  });

  const markVisitedMutation = useMutation({
    mutationFn: ({ resourceId }: { resourceId: string }) =>
      portalApi.markStudentFeedbackResourceVisited(sectionId!, sessionId!, resourceId),
    onSuccess: (_, { resourceId }) => {
      queryClient.setQueryData<StudentAssessmentFeedback>(
        ['portal', 'student', 'feedback', sectionId, sessionId],
        (current) => {
          if (!current) {
            return current;
          }

          return {
            ...current,
            review_skills_json: (current.review_skills_json || []).map((skill) => ({
              ...skill,
              resources: (skill.resources || []).map((resource) =>
                resource.resource_id === resourceId
                  ? { ...resource, visited: true }
                  : resource
              ),
            })),
          };
        }
      );
    },
  });

  const handleResourceVisit = (resource: Resource) => {
    if (!sectionId || !sessionId || resource.visited) {
      return;
    }

    markVisitedMutation.mutate({ resourceId: resource.resource_id });
  };

  if (isLoading) {
    return (
      <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!feedback) {
    return (
      <Box sx={{ py: 4 }}>
        <Alert severity="error">{error ? 'Failed to load feedback' : 'Feedback not found'}</Alert>
      </Box>
    );
  }

  const reviewSkills = feedback.review_skills_json || [];
  const displaySkills = showAllSkills ? reviewSkills : reviewSkills.slice(0, 5);

  return (
    <Box sx={{ py: 4 }}>
      <Stack spacing={3}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h4" fontWeight={700}>Assessment Feedback</Typography>
          <Button variant="outlined" onClick={() => navigate(`/student-portal/classes/${sectionId}`)}>
            Back to Class
          </Button>
        </Stack>

        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>{feedback.assessment_name || 'Assessment'}</Typography>
            <Typography color="text.secondary">Completed: {formatDate(feedback.completed_at)}</Typography>
            <Typography variant="body1" sx={{ mt: 2, whiteSpace: 'pre-line' }}>
              {feedback.ai_feedback_text}
            </Typography>
          </CardContent>
        </Card>

        {reviewSkills.length > 0 && (
          <Card>
            <CardContent>
              <Typography variant="h5" component="h2" gutterBottom>
                Skills to Review
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                The skills below are ordered by priority based on how important each is for Precalculus I and the evidence from your responses. Start with the first skills listed, then work down as time allows.
              </Typography>

              <List>
                {displaySkills.map((skill: SkillMastery, index: number) => (
                  <Accordion key={skill.skill_id} defaultExpanded={index === 0}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box display="flex" alignItems="center" gap={2} width="100%">
                        <TrendingUpIcon color="warning" />
                        <Box flex={1}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>{skill.skill_name}</Typography>
                        </Box>
                        <Chip
                          label={masteryLabels[skill.mastery_level]}
                          color={masteryColors[skill.mastery_level]}
                          size="small"
                        />
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      {skill.resources && skill.resources.length > 0 && (
                        <>
                          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                            Resources To Support This Skill
                          </Typography>
                          <List dense>
                            {skill.resources.slice(0, 3).map((resource: Resource) => (
                              <ListItem key={resource.resource_id} disablePadding>
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                  {resourceIcons[resource.resource_type] || <SchoolIcon />}
                                </ListItemIcon>
                                <ListItemText
                                  primary={(
                                    <Link
                                      href={resource.resource_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      onClick={() => handleResourceVisit(resource)}
                                    >
                                      {resource.resource_title}
                                    </Link>
                                  )}
                                  secondary={resource.resource_type}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </>
                      )}

                      {skill.questions && skill.questions.filter((q) => q.is_blamed).length > 0 && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                            Examples From Your Responses
                          </Typography>
                          <List dense>
                            {skill.questions.filter((q) => q.is_blamed).map((q) => (
                              <Paper key={q.question_id} variant="outlined" sx={{ p: 2, mb: 1.5 }}>
                                <Typography variant="body2" fontWeight={800} gutterBottom>
                                  Question {q.question_number}
                                </Typography>
                                <MathMarkdown sx={{ mb: 1, '& p': { margin: 0 } }}>
                                  {q.question_text}
                                </MathMarkdown>
                                <Box sx={{ pl: 2, borderLeft: 2, borderColor: 'warning.main' }}>
                                  {!q.is_correct && (
                                    <>
                                      {q.user_answer && (
                                        <Box sx={{ color: 'text.secondary', typography: 'body2' }}>
                                          <MathMarkdown>
                                            {`Your answer: $$${q.user_answer}$$`}
                                          </MathMarkdown>
                                        </Box>
                                      )}
                                      <Box sx={{ color: 'success.main', typography: 'body2' }}>
                                        <MathMarkdown>
                                          {`Correct answer: ${q.correct_answer}`}
                                        </MathMarkdown>
                                      </Box>
                                    </>
                                  )}
                                </Box>
                              </Paper>
                            ))}
                          </List>
                        </Box>
                      )}
                    </AccordionDetails>
                  </Accordion>
                ))}
              </List>

              {reviewSkills.length > 5 && (
                <Box textAlign="center" sx={{ mt: 2 }}>
                  <Button onClick={() => setShowAllSkills(!showAllSkills)}>
                    {showAllSkills ? 'Show Less' : `Show ${reviewSkills.length - 5} More Skills`}
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        )}
      </Stack>
    </Box>
  );
}
